// Validation utilities for Add to Cart functionality
// Location: src/features/imprint/composables/useValidation.js

import { computed } from 'vue';

/**
 * Validate quantity against increment rules
 * 
 * WHAT IT VALIDATES:
 * 1. Quantity must be >= 0 (non-negative)
 * 2. Quantity must be >= increment (minimum)
 * 3. Quantity must be divisible by increment (no remainder)
 * 4. Quantity must be <= maxQty (10000)
 * 
 * EXAMPLES:
 * - validateQuantityIncrement(10, 6) → ERROR: "Item purchasable in increments of 6"
 * - validateQuantityIncrement(12, 6) → OK
 * - validateQuantityIncrement(3, 6) → ERROR: "Minimum quantity is 6"
 * - validateQuantityIncrement(11000, 6) → ERROR: "Max quantity is 10000"
 * 
 * This validation is used when:
 * - User manually types quantity in input field
 * - User clicks +/- buttons to change quantity
 * - Before allowing transition from Step 1 to Step 2
 * 
 * @param {number} quantity - The quantity to validate
 * @param {number} increment - The required increment (e.g., 6 means can order 6, 12, 18...)
 * @param {number} maxQty - Maximum allowed quantity (default: 10000)
 * @returns {Object} Validation result with { isValid, error, suggestedQty }
 */
export function validateQuantityIncrement(quantity, increment, maxQty = 10000) {
  // 1. Check if quantity is negative
  if (quantity < 0) {
    return {
      isValid: false,
      error: 'Quantity cannot be negative',
      suggestedQty: 0
    };
  }
  
  // 2. Check if quantity exceeds maximum
  if (quantity > maxQty) {
    return {
      isValid: false,
      error: `Max quantity is ${maxQty}`,
      suggestedQty: maxQty
    };
  }
  
  // 3. Check if quantity is less than minimum (increment)
  if (quantity > 0 && quantity < increment) {
    return {
      isValid: false,
      error: `Minimum quantity is ${increment}`,
      suggestedQty: increment
    };
  }
  
  // 4. Check if quantity is divisible by increment
  if (quantity > 0 && quantity % increment !== 0) {
    const remainder = quantity % increment;
    const suggestedQty = quantity + (increment - remainder);
    
    return {
      isValid: false,
      error: `Item purchasable in increments of ${increment}`,
      suggestedQty: suggestedQty
    };
  }

  return { isValid: true };
}

/**
 * Validate minimum order quantity
 * @param {Object} colorSizeLines - Color and size selections
 * @param {number} minOrderQty - Minimum required quantity
 * @returns {Object} Validation result
 */
export function validateMinimumQuantity(colorSizeLines, minOrderQty) {
  let totalQty = 0;

  Object.values(colorSizeLines).forEach(line => {
    if (line.selectedSizes) {
      totalQty += Object.values(line.selectedSizes).reduce((sum, qty) => sum + (qty || 0), 0);
    }
  });

  if (totalQty < minOrderQty) {
    return {
      isValid: false,
      totalQty,
      error: `Minimum order quantity is ${minOrderQty}. Current total: ${totalQty}`
    };
  }

  return { isValid: true, totalQty };
}

/**
 * Validate that at least one color is selected
 * @param {Object} colorSizeLines - Color and size selections
 * @returns {Object} Validation result
 */
export function validateColorsSelected(colorSizeLines) {
  const hasColorSelected = Object.values(colorSizeLines).some(line => {
    return line.selectedColor && line.selectedColor.id;
  });

  if (!hasColorSelected) {
    return {
      isValid: false,
      error: 'Please select at least one color'
    };
  }

  return { isValid: true };
}

/**
 * Validate that quantities are entered for selected colors
 * @param {Object} colorSizeLines - Color and size selections
 * @returns {Object} Validation result
 */
export function validateQuantitiesEntered(colorSizeLines) {
  const errors = [];

  Object.entries(colorSizeLines).forEach(([lineIndex, line]) => {
    if (line.selectedColor && line.selectedColor.id) {
      const hasQuantity = Object.values(line.selectedSizes || {}).some(qty => qty > 0);
      
      if (!hasQuantity) {
        errors.push({
          lineIndex,
          error: `Please enter quantity for ${line.selectedColor.title}`
        });
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate uploaded image for imprint
 * @param {string} imprintType - Type of imprint (text, image, text_image)
 * @param {Object} uploadedImage - Uploaded image object
 * @param {string} imageColor - Selected color for image
 * @returns {Object} Validation result
 */
export function validateUploadedImage(imprintType, uploadedImage, imageColor) {
  if (imprintType === 'image' || imprintType === 'text_image') {
    if (!uploadedImage) {
      return {
        isValid: false,
        error: 'Please upload an image'
      };
    }
    
    if (!imageColor) {
      return {
        isValid: false,
        error: 'Please select image color'
      };
    }
  }

  return { isValid: true };
}

/**
 * Validate text lines for imprint
 * @param {string} imprintType - Type of imprint
 * @param {Array} lines - Text lines
 * @returns {Object} Validation result
 */
export function validateTextLines(imprintType, lines) {
  if (imprintType === 'text' || imprintType === 'text_image') {
    const errors = [];

    lines.forEach((line, index) => {
      if (line.type === 'message') {
        if (!line.lineMessage || line.lineMessage.trim() === '') {
          errors.push({
            field: `line_${index}_text`,
            message: `Line ${index + 1}: Text is required`
          });
        }
        
        if (!line.selectedColor) {
          errors.push({
            field: `line_${index}_color`,
            message: `Line ${index + 1}: Color is required`
          });
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  return { isValid: true, errors: [] };
}

/**
 * Validate imprint location selection
 * @param {string} selectedLocation - Selected imprint location
 * @returns {Object} Validation result
 */
export function validateImprintLocation(selectedLocation) {
  if (!selectedLocation) {
    return {
      isValid: false,
      error: 'Please select imprint location'
    };
  }

  return { isValid: true };
}

/**
 * Main validation function - validates all requirements before Add to Cart
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} productStore - Product store instance
 * @returns {Object} Complete validation result
 */
export function validateBeforeAddToCart(imprintStore, productStore) {
  const validations = [];

  // 1. Validate colors selected
  const colorValidation = validateColorsSelected(imprintStore.colorSizeLines);
  if (!colorValidation.isValid) {
    validations.push({ 
      type: 'colors', 
      severity: 'error',
      ...colorValidation 
    });
  }

  // 2. Validate quantities entered
  const qtyEnteredValidation = validateQuantitiesEntered(imprintStore.colorSizeLines);
  if (!qtyEnteredValidation.isValid) {
    validations.push({ 
      type: 'quantities', 
      severity: 'error',
      ...qtyEnteredValidation 
    });
  }

  // 3. Validate minimum quantity
  const minQtyValidation = validateMinimumQuantity(
    imprintStore.colorSizeLines,
    productStore.minOrderQty
  );
  if (!minQtyValidation.isValid) {
    validations.push({ 
      type: 'minimum_quantity', 
      severity: 'error',
      ...minQtyValidation 
    });
  }

  // 4. Validate text lines (if applicable)
  if (imprintStore.currentStep >= 4) {
    const textValidation = validateTextLines(
      imprintStore.imprintType,
      imprintStore.imprintMessageColorLines
    );
    if (!textValidation.isValid) {
      validations.push({ 
        type: 'text_lines', 
        severity: 'error',
        ...textValidation 
      });
    }

    // 5. Validate uploaded image (if applicable)
    const imageValidation = validateUploadedImage(
      imprintStore.imprintType,
      imprintStore.uploadedImage,
      imprintStore.imageColor
    );
    if (!imageValidation.isValid) {
      validations.push({ 
        type: 'image', 
        severity: 'error',
        ...imageValidation 
      });
    }

    // 6. Validate imprint location
    const locationValidation = validateImprintLocation(imprintStore.selectedLocation);
    if (!locationValidation.isValid) {
      validations.push({ 
        type: 'location', 
        severity: 'error',
        ...locationValidation 
      });
    }
  }

  return {
    isValid: validations.length === 0,
    validations,
    hasErrors: validations.some(v => v.severity === 'error'),
    hasWarnings: validations.some(v => v.severity === 'warning')
  };
}

/**
 * Composable for using validation in components
 * @param {Object} imprintStore - Imprint store
 * @param {Object} productStore - Product store
 * @returns {Object} Validation utilities
 */
export function useAddToCartValidation(imprintStore, productStore) {
  const validationResult = computed(() => {
    return validateBeforeAddToCart(imprintStore, productStore);
  });

  const canAddToCart = computed(() => {
    return validationResult.value.isValid;
  });

  const validationErrors = computed(() => {
    return validationResult.value.validations.filter(v => v.severity === 'error');
  });

  const validationWarnings = computed(() => {
    return validationResult.value.validations.filter(v => v.severity === 'warning');
  });

  const getErrorMessage = (type) => {
    const validation = validationResult.value.validations.find(v => v.type === type);
    return validation?.error || validation?.message || '';
  };

  const hasError = (type) => {
    return validationErrors.value.some(v => v.type === type);
  };

  return {
    validationResult,
    canAddToCart,
    validationErrors,
    validationWarnings,
    getErrorMessage,
    hasError
  };
}
